/* eslint-disable @typescript-eslint/naming-convention */
// deps
import { useContext, useCallback, useMemo } from "react";

// internal-deps
import { ReviewedCartApiResponse, ApplyCouponsWidgetInfo, GiftingCartApiResponse } from "@swiggy-private/cart-types";
import {
    AnalyticsContext,
    CheckoutActionTypes,
    CheckoutContext,
    CouponPageType,
    GroupOrderCartContext,
    UserContext,
} from "@swiggy-private/checkout-core";

// contexts
import { CartContext } from "@cart-client/contexts/CartContext";
import { UpdateCartContext } from "@cart-client/contexts/UpdateCartContext";
import { CartFlowContext } from "@cart-client/contexts/CartFlowContext";

// hooks
import { useCartRendererAnalytics } from "./useCartRenderedAnalytics";
import { useCrossSellAnalytics } from "./useCrossSellAnalytics";
import { useCartRendererXP } from "./useCartRendererXP";
import { CouponListingContext } from "@cart-client/contexts/CouponListingContext";

// helpers
import { isUserLoggedIn } from "@cart-client/helpers/User";
import { goToLogin } from "@cart-client/helpers/Login";
import { isGiftingCartSource } from "@cart-client/helpers/GiftingCart";
import { isEmpty } from "@swiggy-private/js-utils";
import { getPaymentCouponCode, getRestaurantCouponCode } from "@swiggy-private/cart-ui";
import { getStaticMwebCartPath } from "@cart-client/helpers/Paths";

// analytics
import {
    clickApplyCoupon,
    clickApplyCouponBtnEvent,
    clickTooltipOffercashEvent,
    clickViewMoreCouponsEvent,
} from "@cart-client/analytics/events";

// constants
import { SAVINGS_COUPON } from "@cart-client/constants/Coupon";
import { buildGiftingExtraParamsForSavingsCornerAnalytics } from "@cart-client/helpers/Coupon";
import { extractGiftingCouponCodes, fetchCouponCodeListFromResponse } from "@cart-client/helpers/CouponListing";

export interface UseCouponSavingsActionsReturnType {
    onViewAllCouponsClick: (isCouponLockedWidgetVariant: boolean, isPaymentCouponWidget?: boolean) => void;
    onDirectApplyCouponCtaClick: ({ isPaymentCouponWidget }: { isPaymentCouponWidget?: boolean }) => void;
    onCouponLockedLineItemLoad: VoidFunction;
    onDirectCouponLineItemLoad: (isPaymentCouponWidget?: boolean) => void;
    onApplyCouponSectionClick: ({ isPaymentCouponWidget }: { isPaymentCouponWidget?: boolean }) => void;
    onExtraSavingsTooltipClick: VoidFunction;
    goToCouponListing: VoidFunction;
}

export interface IProps {
    applyCouponWidgetData: ReviewedCartApiResponse["apply_coupon_widget_info"];
    applyPaymentCouponWidgetData: ApplyCouponsWidgetInfo["payment_coupon"];
}

export const useCouponSavingsActions = ({
    applyCouponWidgetData,
    applyPaymentCouponWidgetData,
}: IProps): UseCouponSavingsActionsReturnType => {
    const { cartState } = useContext(CartContext);
    const { reloadCart, syncApplyCouponCart } = useContext(UpdateCartContext);
    const { setUser } = useContext(UserContext);
    const { clickEvent } = useContext(AnalyticsContext);
    const { state: groupCartState } = useContext(GroupOrderCartContext);
    const { resetCouponListingPage } = useContext(CouponListingContext);
    const { setCheckoutState } = useContext(CheckoutContext);
    const { data } = useContext(CartFlowContext);

    const { reviewedCart: cartResponse } = cartState;
    const isGiftingCart = isGiftingCartSource(data?.cartSource);

    const { xpSelectiveCouponVariant } = useCartRendererXP(cartResponse);
    const { onImpressionAddMoreAndSaveMore } = useCrossSellAnalytics(cartResponse);
    const { onImpressionDirectCouponWidgetEvent } = useCartRendererAnalytics(cartResponse);

    const userLoggedIn = isUserLoggedIn();
    const couponCode = getRestaurantCouponCode(cartResponse?.data, applyCouponWidgetData);
    const paymentCouponCode = getPaymentCouponCode(cartResponse?.data, applyPaymentCouponWidgetData);

    const [analyticsExtraParams, analyticsContext] = useMemo(() => {
        if (isGiftingCart) {
            return [
                buildGiftingExtraParamsForSavingsCornerAnalytics(cartResponse?.data as GiftingCartApiResponse),
                fetchCouponCodeListFromResponse(cartResponse?.data as GiftingCartApiResponse),
            ];
        }
        return [{}, {}];
    }, [cartResponse, isGiftingCart]);

    const groupCartId = groupCartState.data?.group_cart_id;

    const goToCouponListing = useCallback(
        (isPaymentCouponWidget?: boolean) => {
            resetCouponListingPage();

            const actionType = isGiftingCart
                ? CheckoutActionTypes.SHOW_GIFTING_COUPON_LISTING
                : CheckoutActionTypes.SHOW_COUPON_LISTING;

            setCheckoutState({
                type: actionType,
                payload: {
                    couponListingLaunchTimestamp: Date.now(),
                    couponListingType: isPaymentCouponWidget
                        ? CouponPageType.PAYMENT_COUPON_LISTING
                        : CouponPageType.RESTAURANT_COUPON_LISTING,
                },
            });
        },
        [isGiftingCart, resetCouponListingPage, setCheckoutState],
    );
    const goToLoginFromSavings = useCallback(async () => {
        await goToLogin(SAVINGS_COUPON, reloadCart, setUser, getStaticMwebCartPath());
    }, [reloadCart, setUser]);

    const onApplyCouponSectionClick = useCallback(
        async ({ isPaymentCouponWidget = false }: { isPaymentCouponWidget?: boolean }) => {
            if (!userLoggedIn) {
                await goToLoginFromSavings();
                return;
            }

            clickApplyCoupon(clickEvent, {
                xpSelectiveCouponVariant,
                groupCartId,
                isPaymentCouponWidget,
                extraContext: analyticsContext,
                extraParams: analyticsExtraParams,
            });
            goToCouponListing(isPaymentCouponWidget);
        },
        [
            clickEvent,
            goToCouponListing,
            goToLoginFromSavings,
            groupCartId,
            userLoggedIn,
            xpSelectiveCouponVariant,
            analyticsContext,
            analyticsExtraParams,
        ],
    );

    const onCouponLockedLineItemLoad = useCallback(() => {
        onImpressionAddMoreAndSaveMore(true);
    }, [onImpressionAddMoreAndSaveMore]);

    const onViewAllCouponsClick = useCallback(
        async (isCouponLockedWidgetVariant: boolean, isPaymentCouponWidget?: boolean) => {
            if (!userLoggedIn) {
                await goToLoginFromSavings();
                return;
            }

            if (isCouponLockedWidgetVariant) {
                clickViewMoreCouponsEvent(clickEvent, {
                    groupCartId,
                    extraContext: analyticsContext,
                    extraParams: analyticsExtraParams,
                });
            } else {
                clickApplyCoupon(clickEvent, {
                    xpSelectiveCouponVariant,
                    groupCartId,
                    isPaymentCouponWidget,
                    extraContext: analyticsContext,
                    extraParams: analyticsExtraParams,
                });
            }

            goToCouponListing(isPaymentCouponWidget);
        },
        [
            clickEvent,
            goToCouponListing,
            goToLoginFromSavings,
            groupCartId,
            userLoggedIn,
            xpSelectiveCouponVariant,
            analyticsContext,
            analyticsExtraParams,
        ],
    );

    const getCouponCode = useCallback(
        (isPaymentCouponWidget?: boolean) => {
            return isPaymentCouponWidget ? paymentCouponCode : couponCode;
        },
        [couponCode, paymentCouponCode],
    );

    const giftingCouponCodes = useMemo(() => {
        return isGiftingCartSource(data?.cartSource) ? extractGiftingCouponCodes(applyCouponWidgetData) : [];
    }, [applyCouponWidgetData, data?.cartSource]);

    const onDirectCouponLineItemLoad = useCallback(
        (isPaymentCouponWidget?: boolean) => {
            const couponCode = getCouponCode(isPaymentCouponWidget);
            if (!isEmpty(couponCode)) {
                onImpressionDirectCouponWidgetEvent(couponCode, isPaymentCouponWidget);
            }
        },
        [getCouponCode, onImpressionDirectCouponWidgetEvent],
    );

    const onDirectApplyCouponCtaClick = useCallback(
        async ({ isPaymentCouponWidget = false }: { isPaymentCouponWidget?: boolean }) => {
            if (!userLoggedIn) {
                await goToLoginFromSavings();
                return;
            }
            const couponCode = getCouponCode(isPaymentCouponWidget);
            if (!isEmpty(couponCode)) {
                clickApplyCouponBtnEvent(clickEvent, {
                    couponCode,
                    groupCartId,
                    isPaymentCouponWidget,
                    extraParams: analyticsExtraParams,
                    extraContext: analyticsContext,
                });
                syncApplyCouponCart({ couponCode });
            } else if (!isEmpty(giftingCouponCodes)) {
                clickApplyCouponBtnEvent(clickEvent, {
                    groupCartId,
                    isPaymentCouponWidget,
                    extraParams: analyticsExtraParams,
                    extraContext: analyticsContext,
                });
                syncApplyCouponCart({ appliedCouponCodes: giftingCouponCodes });
            }
        },
        [
            userLoggedIn,
            getCouponCode,
            giftingCouponCodes,
            goToLoginFromSavings,
            clickEvent,
            groupCartId,
            analyticsExtraParams,
            analyticsContext,
            syncApplyCouponCart,
        ],
    );

    const onExtraSavingsTooltipClick = useCallback(() => {
        clickTooltipOffercashEvent(applyCouponWidgetData?.coupon_code ?? "", clickEvent);
    }, [applyCouponWidgetData?.coupon_code, clickEvent]);

    return {
        onViewAllCouponsClick,
        onDirectApplyCouponCtaClick,
        onCouponLockedLineItemLoad,
        onDirectCouponLineItemLoad,
        onApplyCouponSectionClick,
        onExtraSavingsTooltipClick,
        goToCouponListing,
    };
};
